import { ReadonlyVec3 } from 'gl-matrix';
import {
    ContourGeometricType,
    DicomContour,
    DicomStructureSet,
} from '~/domain/dicom/dicomstructureset';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { Image3D } from '~/domain/patient/image3d';

/**
 * A single per-slice loop to be written into an ROI, keyed by the slice's
 * normal position in mm along the image grid's Z axis (see
 * {@link Image3D.getUidForSlice}). `points` is patient-frame mm and MUST be
 * an open ring — {@link ContourGeometricType.CLOSED_PLANAR} is the DICOM
 * semantic, not a repeated closing vertex, and a duplicated last point
 * becomes a zero-length segment for the dose engine.
 */
export interface RoiContourInput {
    normalPosition: number;
    points: ReadonlyArray<ReadonlyVec3>;
}

/**
 * Writes explicit `CLOSED_PLANAR` contours into `roi`, one per entry in
 * `contours`, and refreshes `structureSet`'s contour sequence so the
 * geometry is included on export.
 *
 * This is the only place in the app that authors NEW contour geometry
 * from a model rather than copying, transforming or rasterising existing
 * contours — every other structure-producing operation goes through a
 * GPU signed-distance-field texture, which is unsuitable here: a couch shell is a thin
 * wall a few millimetres thick, and round-tripping it through an SDF at
 * image-grid resolution would degrade exactly the geometry a dose engine
 * integrates through.
 *
 * Resolves every slice's Referenced SOP Instance UID from `image`, and
 * validates every resulting loop via `DicomContour.isValid` (more than 2
 * points, not all collinear) — BOTH fully complete BEFORE writing anything
 * to `roi`. If any `normalPosition` has no matching slice, or any loop is
 * degenerate, this throws and leaves `roi.contours` completely untouched —
 * a partially written or geometrically-corrupt ROI is clinically worse than
 * none, since a TPS consuming it would compute dose through it rather than
 * seeing an obviously absent structure. Also rejects an empty `contours`
 * array rather than silently clearing the ROI's existing geometry.
 *
 * Does NOT mark the change tracker. `DicomRoi.contours`'s setter is a plain
 * assignment with no tracker side effect, so the caller MUST perform this
 * call inside `tracker.command(...)` for the write to be tracked/undoable
 * and for downstream listeners to see it.
 */
export function writeRoiContours(
    structureSet: DicomStructureSet,
    roi: DicomRoi,
    image: Image3D,
    contours: ReadonlyArray<RoiContourInput>
): void {
    // An empty array would silently wipe whatever geometry the ROI already
    // has via the unconditional assignment below. "Write contours" never
    // implies "clear the ROI" — a caller that means that should call a
    // clearing operation explicitly, not pass an empty list here.
    if (contours.length === 0) {
        throw new Error(
            'writeRoiContours: contours must not be empty; refusing to ' +
                'silently clear the ROI'
        );
    }

    const sopClassUid = structureSet.referencedSopClassUid;

    // Resolve-all-then-write: map to UIDs first so a missing UID throws
    // before a single DicomContour is constructed or assigned.
    const resolved = contours.map((contour) => {
        const sopInstanceUid = image.getUidForSlice(contour.normalPosition);
        if (sopInstanceUid === undefined) {
            // No SOP UIDs/patient/series identifiers in this message — only
            // the numeric slice position, which carries no PHI.
            throw new Error(
                `writeRoiContours: no image slice found at normal position ` +
                    `${contour.normalPosition}mm; cannot resolve a Referenced ` +
                    `SOP Instance UID for this contour`
            );
        }
        return { contour, sopInstanceUid };
    });

    // Constructing a DicomContour is side-effect free, so build every
    // contour before validating any of them, and validate every one
    // before assigning any of them to `roi` — resolve/build/validate all
    // fully precede the single write, preserving atomicity for this
    // failure path exactly as for the missing-UID one above.
    const built = resolved.map(
        ({ contour, sopInstanceUid }) =>
            new DicomContour(
                ContourGeometricType.CLOSED_PLANAR,
                contour.points,
                sopClassUid,
                sopInstanceUid
            )
    );

    for (const [i, dicomContour] of built.entries()) {
        // DicomContour's constructor validates nothing; `isValid` is the
        // one place that actually checks the geometry (>2 points, not all
        // collinear). Model-authored geometry gets no other validation
        // before export, so a degenerate loop must fail loudly here
        // rather than reach a dose engine as silently-corrupt
        // CLOSED_PLANAR geometry.
        if (!dicomContour.isValid) {
            throw new Error(
                `writeRoiContours: contour at normal position ` +
                    `${contours[i].normalPosition}mm is not a valid closed ` +
                    `loop (needs more than 2 points and must not be ` +
                    `collinear); refusing to write it`
            );
        }
    }

    roi.contours = built;

    structureSet.updateContourSequence(roi.roiNumber);
}
